import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	Inject,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BreadcrumbItem, BreadcrumbService } from "@obg/core.breadcrumb";
import { CustomerSupportService } from "@obg/core.customer-support";
import { HomeConfigService } from "@obg/core.home";
import {
	AppStartupContext,
	CORE_ROUTE,
	CommandDispatcherAction,
	CommandType,
	STARTUP_CONTEXT_TOKEN,
} from "@obg/ngx.core";
import { CoreContentState, TranslationSelector } from "@obg/ngx.core.content";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ScrollToTopOptions } from "@obg/ngx.ui.global.scrollbar";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, merge, Observable, Subscription } from "rxjs";
import { map, tap } from "rxjs/operators";

import { DynamicUiService } from "../../dynamic-ui/dynamic-ui.service";

import { CoreLayoutRouteData, FOOTER_TRANSLATIONS } from "./core-layout.model";

@Component({
	selector: "obg-m-core-layout",
	templateUrl: "./core-layout.container.html",
	styleUrls: ["./core-layout.container.scss"],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoreLayoutContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-core-layout") hostCssClass = true;
	@HostBinding("class.app-theme-dark") isDarkTheme = false;
	@HostBinding("class.app-theme-light") isLightTheme = false;
	@HostBinding("class.app-theme-desktop") isDesktop = true;

	marginTop: string | undefined;
	height: string | undefined;
	showSeoFooter = false;
	scrollToTopOptions: ScrollToTopOptions = {
		routeChange: true,
		commandTrigger: true,
	};

	breadcrumbData$: Observable<BreadcrumbItem[] | undefined> | undefined;
	translations$: Observable<Record<string, string | undefined>> | undefined;

	private data$$: Subscription | undefined;
	readonly showFabricFooter$ = this.homeConfigService
		.getMFeFooterEnabled$()
		.pipe(tap(() => this.changeDetectorRef.markForCheck()));

	constructor(
		private store: Store<CoreContentState>,
		private dynamicUiService: DynamicUiService,
		private breadcrumbService: BreadcrumbService,
		private translateService: TranslateService,
		private changeDetectorRef: ChangeDetectorRef,
		private translationSelector: TranslationSelector,
		private commandDispatcherAction: CommandDispatcherAction,
		private customerSupportService: CustomerSupportService,
		private homeConfigService: HomeConfigService,

		@Inject(STARTUP_CONTEXT_TOKEN) startupContext: AppStartupContext
	) {
		this.isDarkTheme =
			startupContext.brandName.includes("betsafe") || startupContext.brandName.includes("nordicbet");
		this.isLightTheme = !this.isDarkTheme;
	}

	ngOnInit() {
		const height$ = this.dynamicUiService
			.headerHeight$()
			.pipe(tap((height) => ([this.marginTop, this.height] = [`${height}px`, `calc(100vh - ${height}px)`])));

		const route$ = this.store
			.select<CoreLayoutRouteData | undefined>(RouteSelector.getData)
			.pipe(tap((data) => (this.showSeoFooter = !!data?.showSeoFooter)));

		this.breadcrumbData$ = combineLatest([
			this.store.select<CoreLayoutRouteData | undefined>(RouteSelector.getData),
			this.store.select(RouteSelector.getCurrent).pipe(ignoreNil()),
		]).pipe(
			map(([data, route]) =>
				data?.hideBreadcrumb
					? undefined
					: this.breadcrumbService.buildList({
							currentRoute: route,
							rootItem: {
								label: this.translateService.instant("general.breadcrumb.item.root").toLowerCase(),
								route: "/",
							},
						})
			)
		);

		this.data$$ = merge(height$, route$).subscribe(() => this.changeDetectorRef.markForCheck());

		this.translations$ = this.store.pipe(
			this.translationSelector.getByKeys_v2$(FOOTER_TRANSLATIONS.translations, ["common", "general"]),
			map((keys) => Object.fromEntries(keys.map((_, i) => [FOOTER_TRANSLATIONS.translations[i], keys[i]])))
		);
	}

	ngOnDestroy() {
		if (this.data$$) {
			this.data$$.unsubscribe();
		}
	}

	handleSupportButton(event: any) {
		switch (event.detail) {
			case "footer.other-links.cookie-settings":
				this.store.dispatch(RouteAction.navigate(CORE_ROUTE.cookieSettings));
				break;
			case "footer.other-links.help":
				this.customerSupportService.openSupportPageInNewTab();
				break;
			case "footer.other-links.back-to-top":
				this.store.dispatch(this.commandDispatcherAction.trigger(CommandType.BackToTop));
				break;
		}
	}

	handleSearch() {
		this.store.dispatch(RouteAction.navigate(CORE_ROUTE.casino.gameSearch));
	}
}
